import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_Z_INDEX = 1100;
const BACKDROP_Z_INDEX = 1090;

function getAnchorNode(anchorRef, anchorEl) {
    if (anchorEl) return anchorEl;
    return anchorRef?.current || null;
}

/**
 * Renders a dropdown/menu on document.body so it is not clipped or stacked
 * behind later siblings (overflow, stacking contexts, etc.).
 */
function clampMenuBox(top, left, menuWidth, menuHeight, viewportPadding) {
    const maxTop = window.innerHeight - Math.min(menuHeight || 0, window.innerHeight - viewportPadding * 2) - viewportPadding;
    const nextTop = Math.max(viewportPadding, Math.min(top, maxTop));
    const nextLeft = Math.max(viewportPadding, Math.min(left, window.innerWidth - menuWidth - viewportPadding));
    return { top: nextTop, left: nextLeft };
}

function PortaledMenu({
    isOpen,
    onClose,
    anchorRef,
    anchorEl,
    position = null,
    children,
    className = '',
    align = 'left',
    matchAnchorWidth = false,
    offsetY = 8,
    role = 'menu'
}) {
    const menuRef = useRef(null);
    const [style, setStyle] = useState({
        position: 'fixed',
        zIndex: MENU_Z_INDEX,
        visibility: 'hidden'
    });

    const positionX = position?.x;
    const positionY = position?.y;

    const updatePosition = useCallback(() => {
        const menuEl = menuRef.current;
        const viewportPadding = 8;
        const menuHeight = menuEl?.offsetHeight || 0;

        if (Number.isFinite(positionX) && Number.isFinite(positionY)) {
            const menuWidth = menuEl?.offsetWidth || 192;
            let top = positionY;
            let left = positionX;
            if (menuHeight > 0 && top + menuHeight > window.innerHeight - viewportPadding) {
                top = positionY - menuHeight;
            }
            if (left + menuWidth > window.innerWidth - viewportPadding) {
                left = positionX - menuWidth;
            }
            const clamped = clampMenuBox(top, left, menuWidth, menuHeight, viewportPadding);
            setStyle({
                position: 'fixed',
                top: `${clamped.top}px`,
                left: `${clamped.left}px`,
                zIndex: MENU_Z_INDEX,
                visibility: 'visible'
            });
            return;
        }

        const anchor = getAnchorNode(anchorRef, anchorEl);
        if (!anchor) return;

        const rect = anchor.getBoundingClientRect();
        const menuWidth = matchAnchorWidth
            ? rect.width
            : (menuEl?.offsetWidth || 192);
        const gap = offsetY;

        let top = rect.bottom + gap;
        if (menuHeight > 0) {
            const spaceBelow = window.innerHeight - rect.bottom - gap - viewportPadding;
            const spaceAbove = rect.top - gap - viewportPadding;
            if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
                top = rect.top - menuHeight - gap;
            }
        }

        let left = align === 'right' ? rect.right - menuWidth : rect.left;
        const clamped = clampMenuBox(top, left, menuWidth, menuHeight, viewportPadding);

        const next = {
            position: 'fixed',
            top: `${clamped.top}px`,
            left: `${clamped.left}px`,
            zIndex: MENU_Z_INDEX,
            visibility: 'visible'
        };
        if (matchAnchorWidth) {
            next.width = `${rect.width}px`;
        }
        setStyle(next);
    }, [align, anchorEl, anchorRef, matchAnchorWidth, offsetY, positionX, positionY]);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        updatePosition();
        const menuEl = menuRef.current;
        const observer = menuEl && typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(updatePosition)
            : null;
        if (menuEl && observer) observer.observe(menuEl);

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div>
            <div
                className="fixed inset-0"
                style={{ zIndex: BACKDROP_Z_INDEX }}
                onClick={onClose}
                onContextMenu={(event) => {
                    event.preventDefault();
                    onClose?.();
                }}
                aria-hidden="true"
            />
            <div ref={menuRef} role={role} className={className} style={style}>
                {children}
            </div>
        </div>,
        document.body
    );
}

export default PortaledMenu;
